import { useState } from 'react';
import { LoginCredentials } from '../../domain/entities/token_entity';
import { LoginUseCase } from '../../domain/usecases/login_usecase';
import { LogoutUseCase } from '../../domain/usecases/logout_usecase';
import { GetPersistedTokenUseCase } from '../../domain/usecases/get_persisted_token_usecase';

/**
 * Controller para operações de autenticação
 * Gerencia o estado da UI e coordena com os casos de uso
 */
export class AuthController {
  private loginUseCase: LoginUseCase;
  private logoutUseCase: LogoutUseCase;
  private getPersistedTokenUseCase: GetPersistedTokenUseCase;

  constructor(
    loginUseCase: LoginUseCase,
    logoutUseCase: LogoutUseCase,
    getPersistedTokenUseCase: GetPersistedTokenUseCase
  ) {
    this.loginUseCase = loginUseCase;
    this.logoutUseCase = logoutUseCase;
    this.getPersistedTokenUseCase = getPersistedTokenUseCase;
  }

  async login(credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> {
    try {
      await this.loginUseCase.execute(credentials);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Login failed' 
      };
    }
  }

  async logout(): Promise<{ success: boolean; error?: string }> {
    try {
      await this.logoutUseCase.execute();
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Logout failed' 
      };
    }
  }

  async getPersistedToken(): Promise<{ token: string | null; error?: string }> {
    try {
      const token = await this.getPersistedTokenUseCase.execute();
      return { token: token?.token || null };
    } catch (error) {
      return { 
        token: null, 
        error: error instanceof Error ? error.message : 'Failed to get token' 
      };
    }
  }
}

/**
 * Hook customizado para usar o AuthController
 */
export const useAuthController = (controller: AuthController) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatAuthError = (raw?: string | null): string | null => {
    if (!raw) return null;
    // Mensagens recebidas podem conter JSON ou prefixos repetidos.
    // Normaliza casos comuns e limita comprimento para exibição.
    let msg = raw;

    // Remover prefixos repetitivos
    msg = msg.replace(/^(Login request failed:\s*)+/i, '');
    msg = msg.replace(/^(Login failed:\s*)+/i, '');

    // Tentar extrair mensagem JSON se o raw for JSON-like
    try {
      if (msg.trim().startsWith('{')) {
        const parsed = JSON.parse(msg);
        if (parsed && (parsed.message || parsed.error)) {
          msg = parsed.message || parsed.error;
        } else if (parsed && parsed.status && parsed.message) {
          msg = parsed.message;
        } else {
          msg = 'Falha na autenticação.';
        }
      }
    } catch (e) {
      // não é JSON, ignora
    }

    // Mensagens muito longas são truncadas
    if (msg.length > 180) {
      msg = msg.slice(0, 177) + '...';
    }

    return msg;
  };

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    const result = await controller.login(credentials);

    if (!result.success) {
      const friendly = formatAuthError(result.error || 'Falha ao fazer login.');
      setError(friendly);
    }

    setIsLoading(false);
    return result;
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);
    
    const result = await controller.logout();
    
    if (!result.success) {
      setError(result.error || 'Logout failed');
    }
    
    setIsLoading(false);
    return result;
  };

  const getPersistedToken = async () => {
    const result = await controller.getPersistedToken();
    
    if (result.error) {
      setError(result.error);
    }
    
    return result;
  };

  const clearError = () => setError(null);

  return {
    isLoading,
    error,
    login,
    logout,
    getPersistedToken,
    clearError
  };
};
